import { motion } from 'framer-motion';
import { useEffect, useRef, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import ArrowBlackLeft from '../../assets/arrow_black_left.svg';
import ArrowBlackRight from '../../assets/arrow_black_right.svg';
import IntroduceSection2Image1 from '../../assets/introduce_section2_image1.png';
import IntroduceSection2Image2 from '../../assets/introduce_section2_image2.png';
import IntroduceSection2Image3 from '../../assets/introduce_section2_image3.png';
import IntroduceSection2Image4 from '../../assets/introduce_section2_image4.png';
import IntroduceSection2Image5 from '../../assets/introduce_section2_image5.png';
import IntroduceSection2Image6 from '../../assets/introduce_section2_image6.png';
import { HorizontalLayout, useResponsivePadding, VerticalLayout } from '../../common/common-layout';
import { H2, H5, Text1, Text3 } from '../../common/common-text';
import BTBox from '../../common/components/BTBox';
import i18n from '../../common/i18n/i18n';
import { pxToRem } from '../../common/utils/ConvertUtils';
import '../../css/bbpage.css';
import '../../css/coomon.css';
import { useDeviceType } from '../../hooks/useDeviceType';

// 🔸 isInView.section2 을 props로 받음
type IntroduceSection2Props = {
  isInView: boolean;
  onScrollEndChange?: (isEnd: boolean, isStart: boolean) => void;
  scrollRatio?: number;
};

const IntroduceSection2 = ({ isInView, onScrollEndChange, scrollRatio = 0, }: IntroduceSection2Props) => {
  const { t } = useTranslation();
  const SCROLL_THRESHOLD = 50; // deltaY가 이 이상일 때만 감지
  const SCROLL_COOLDOWN_MS = 1000;
  let lastScrollTime = 0;

  const { horizontal: padding } = useResponsivePadding();
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const sectionRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [isHorizontalScroll, setIsHorizontalScroll] = useState(false);
  const [isStart, setIsStart] = useState(false);
  const [isEnd, setIsEnd] = useState(false);
  const navigate = useNavigate();
  const { isMobile, isDesktop, isTablet } = useDeviceType();

  const [canLeft, setCanLeft] = useState(false);
  const [canRight, setCanRight] = useState(true);

  const isEng = ['en', 'en-mobile'].includes(i18n.language);
  const isChina = ['zh', 'zh-mobile'].includes(i18n.language);

  useEffect(() => {
    if (onScrollEndChange) {
      onScrollEndChange(isStart, isEnd);
    }
  }, [isStart, isEnd, onScrollEndChange]);

  const scrollStep = (dir: 'left' | 'right') => {
    const el = scrollContainerRef.current;
    if (!el) return;

    // 카드 1장 + 간격 정도로 이동 (대략 520px). 화면 폭 기준으로 이동하고 싶으면 el.clientWidth * 0.9 등으로 바꿔도 OK.
    const step = 520;
    const delta = dir === 'left' ? -step : step;

    el.scrollBy({ left: delta, behavior: 'smooth' });
  };

  useEffect(() => {
    const el = scrollContainerRef.current;
    if (!el) return;

    const onScroll = () => {
      const { scrollLeft, scrollWidth, clientWidth } = el;
      setCanLeft(scrollLeft > 0);
      setCanRight(scrollLeft + clientWidth < scrollWidth - 1);
    };

    onScroll(); // 초기 상태
    el.addEventListener('scroll', onScroll, { passive: true });
    return () => el.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <VerticalLayout style={{ background: 'white' }} ref={containerRef}>
      <BTBox height={isMobile ? 40 : pxToRem(91)} />
      <div style={{ overflow: 'hidden' }}>
        <motion.div
          initial={{ y: 100, opacity: 1 }}
          animate={isInView ? { y: 0, opacity: 1 } : {}}
          transition={{ duration: 0.9, ease: 'easeOut' }}
        >
          <div style={{ paddingLeft: padding, paddingRight: padding }}>
            <H2 style={{ fontSize: pxToRem(isTablet ? 68 : 62) }}>
              Solutions & Services
            </H2>
          </div>
        </motion.div>
      </div>

      <BTBox height={isMobile ? 8 : pxToRem(24)} />
      <div style={{ overflow: 'hidden', paddingBottom: '10px' }}>
        <motion.div
          initial={{ y: 300, opacity: 0 }}
          animate={isInView ? { y: 0, opacity: 1 } : {}}
          transition={{ duration: 0.9, ease: 'easeOut', delay: 0.2 }}
        >
          <div style={{ paddingLeft: padding, paddingRight: padding }}>
            <Text1 style={{ opacity: 0.5, wordBreak: 'keep-all' }}>
              <Trans i18nKey="비비테크의 Solutions & Services는 기술을 바탕으로 산업의 가능성을 확장하며, 고객의 비즈니스 혁신을 실현합니다. 복잡한 과제를 간결하게 풀어내는 솔루션으로, 지속 가능한 미래를 함께 만들어갑니다." />
            </Text1>
          </div>
        </motion.div>
      </div>

      <BTBox height={isMobile ? 16 : pxToRem(43)} />

      {!isMobile && (
        <HorizontalLayout>
          <button
            type="button"
            onClick={() => scrollStep('left')}
            disabled={!canLeft}
            style={{
              position: 'absolute',
              left: padding - (isMobile ? 22 : isDesktop ? 45 : 34),
              transform: `translateY(320%)`,
              width: pxToRem(90),
              height: pxToRem(90),
              borderRadius: pxToRem(45),
              color: '#fff',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              outline: 'none',
              border: 'none',
            }}
            className="scroll-button"
            data-analytics-name="솔루션서비스_좌측스크롤"
          >
            <img src={ArrowBlackLeft} style={{ width: pxToRem(32), height: pxToRem(18) }} />
          </button>
          <div style={{ flex: 1 }} />
          <button
            type="button"
            onClick={() => scrollStep('right')}
            disabled={!canRight}
            style={{
              position: 'absolute',
              right: padding - (isMobile ? 22 : isDesktop ? 45 : 34),
              transform: `translateY(320%)`,
              width: pxToRem(90),
              height: pxToRem(90),
              borderRadius: pxToRem(45),
              color: '#fff',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              outline: 'none',
              border: 'none',
            }}
            className="scroll-button"
            data-analytics-name="솔루션서비스_우측스크롤"
          >
            <img src={ArrowBlackRight} style={{ width: pxToRem(32), height: pxToRem(18) }} />
          </button>
        </HorizontalLayout>
      )}

      <div style={{ width: '100%', height: 'auto', overflowX: 'auto', whiteSpace: 'nowrap' }}>
        <div ref={sectionRef} style={{ height: 'auto', overflow: 'hidden' }}>
          <div
            ref={scrollContainerRef}
            style={{
              display: 'flex',
              overflowX: 'auto',
              width: '100%',
              height: '100%',
              whiteSpace: 'nowrap',
              scrollBehavior: 'smooth',
              scrollbarWidth: 'none',
              msOverflowStyle: 'none',
              WebkitOverflowScrolling: 'touch',
              scrollSnapType: 'x mandatory',
              scrollPaddingLeft: (padding - 16),
              overscrollBehaviorX: 'contain',
            }}
          >
            <HorizontalLayout wrapContents={true}>
              <VerticalLayout paddingLeft={padding} wrapContents={true} onClick={() => { navigate('/business'); }} style={{ cursor: 'pointer', scrollSnapAlign: 'start', scrollSnapStop: 'always' }} data-analytics-name="솔루션서비스_반도체">
                <img src={IntroduceSection2Image1} style={{ width: isMobile ? '271px' : `${pxToRem(504)}`, height: isMobile ? '302px' : `${pxToRem(560)}` }} />
                <BTBox height={pxToRem(30)} />
                <H5 color='black' style={{ width: isMobile ? '271px' : `${pxToRem(496)}`, height: pxToRem(90) }}>
                  Semiconductor<br />Main Utility & Process Piping
                </H5>
                <BTBox height={pxToRem(13)} />
                <Text3 color='#979797' style={{
                  width: isMobile ? '271px' : `${pxToRem(496)}`,
                  ...(isMobile && isEng) ? {fontSize: '12px'} : {}
                }}>{t('반도체 생산 환경에 최적화된 Total Solution 제공')}</Text3>
              </VerticalLayout>
              <VerticalLayout paddingLeft={isMobile ? 16 : pxToRem(24)} wrapContents={true} onClick={() => { navigate('/business/battery'); }} style={{ cursor: 'pointer', scrollSnapAlign: 'start', scrollSnapStop: 'always' }} data-analytics-name="솔루션서비스_이차전지">
                <img src={IntroduceSection2Image2} style={{ width: isMobile ? '271px' : `${pxToRem(504)}`, height: isMobile ? '302px' : `${pxToRem(560)}` }} />
                <BTBox height={pxToRem(30)} />
                <H5 color='black' style={{ width: isMobile ? '271px' : `${pxToRem(496)}`, display: 'block', height: pxToRem(90) }}>
                  Lithium-Ion Battery<br />Main Utility & Process Piping
                </H5>
                <BTBox height={pxToRem(13)} />
                <Text3 color='#979797' style={{ width: isMobile ? '271px' : `${pxToRem(496)}`,
                  ...(isMobile && isEng) ? {fontSize: '12px'} : {} }}>{t('차세대 에너지 산업 인프라 기술 보유')}</Text3>
              </VerticalLayout>
              <VerticalLayout paddingLeft={isMobile ? 16 : pxToRem(24)} wrapContents={true} onClick={() => { navigate('business/datacenter'); }} style={{ cursor: 'pointer', scrollSnapAlign: 'start', scrollSnapStop: 'always' }} data-analytics-name="솔루션서비스_데이터센터">
                <img src={IntroduceSection2Image6} style={{ width: isMobile ? '271px' : `${pxToRem(504)}`, height: isMobile ? '302px' : `${pxToRem(560)}` }} />
                <BTBox height={pxToRem(30)} />
                <H5 color='black' style={{ width: isMobile ? '271px' : `${pxToRem(496)}`, height: pxToRem(90) }}>
                  Data Center<br />Utility Engineering
                </H5>
                <BTBox height={pxToRem(13)} />
                <Text3 color='#979797' style={{ width: isMobile ? '271px' : `${pxToRem(496)}`,
                  ...(isMobile && isEng) ? {fontSize: '12px'} : {} }}>{t('모든 데이터 센터 방식에 부합하는 엔지니어링 제공')}</Text3>
              </VerticalLayout>
              <VerticalLayout paddingLeft={isMobile ? 16 : pxToRem(24)} wrapContents={true} onClick={() => { navigate('/business/dryroom'); }} style={{ cursor: 'pointer', scrollSnapAlign: 'start', scrollSnapStop: 'always' }} data-analytics-name="솔루션서비스_클린룸드라이룸">
                <img src={IntroduceSection2Image3} style={{ width: isMobile ? '271px' : `${pxToRem(504)}`, height: isMobile ? '302px' : `${pxToRem(560)}` }} />
                <BTBox height={pxToRem(30)} />
                <H5 color='black' style={{ width: isMobile ? '271px' : `${pxToRem(496)}` }}>
                  Clean Room & Dry Room Engineering
                </H5>
                <BTBox height={pxToRem(13)} />
                <Text3 color='#979797' style={{ width: isMobile ? '271px' : `${pxToRem(496)}`,
                  ...(isMobile && isEng) ? {fontSize: '12px'} : {} }}>{t('민감한 공정 환경에 완벽한 솔루션 제공')}</Text3>
              </VerticalLayout>
              <VerticalLayout paddingLeft={isMobile ? 16 : pxToRem(24)} wrapContents={true} onClick={() => { navigate('/business/teflon'); }} style={{ cursor: 'pointer', scrollSnapAlign: 'start', scrollSnapStop: 'always' }} data-analytics-name="솔루션서비스_테프론코팅">
                <img src={IntroduceSection2Image4} style={{ width: isMobile ? '271px' : `${pxToRem(504)}`, height: isMobile ? '302px' : `${pxToRem(560)}` }} />
                <BTBox height={pxToRem(30)} />
                <H5 color='black' style={{ width: isMobile ? '271px' : `${pxToRem(496)}`, height: pxToRem(90) }}>
                  Teflon Coating Piping
                </H5>
                <BTBox height={pxToRem(13)} />
                <Text3 color='#979797' style={{ width: isMobile ? '271px' : `${pxToRem(496)}`,
                  ...(isMobile && isEng) ? {fontSize: '12px'} : {} }}>{t('장비의 수명과 공정 안정성을 높이는 핵심 기술 보유')}</Text3>
              </VerticalLayout>
              <VerticalLayout paddingLeft={isMobile ? 16 : pxToRem(24)} paddingRight={padding} wrapContents={true} onClick={() => { navigate('/business/cryogenic'); }} style={{ cursor: 'pointer', scrollSnapAlign: 'start', scrollSnapStop: 'always' }} data-analytics-name="솔루션서비스_극저온배관">
                <img src={IntroduceSection2Image5} style={{ width: isMobile ? '271px' : `${pxToRem(504)}`, height: isMobile ? '302px' : `${pxToRem(560)}` }} />
                <BTBox height={pxToRem(30)} />
                <H5 color='black' style={{ width: isMobile ? '271px' : `${pxToRem(496)}`, height: pxToRem(90) }}>
                  Cryogenic Piping
                </H5>
                <BTBox height={pxToRem(13)} />
                <Text3 color='#979797' style={{ width: isMobile ? '271px' : `${pxToRem(496)}`,
                  ...(isMobile && isEng) ? {fontSize: '12px'} : {} }}>{t('극저온 유체 운송에 최적화된 고내구성 배관 시스템 구축')}</Text3>
              </VerticalLayout>
            </HorizontalLayout>
          </div>
        </div>
      </div>

      <BTBox height={pxToRem(142)} />
    </VerticalLayout>
  );
};

export default IntroduceSection2;
